/**
 * Custom Select dropdown — replaces native <select> with a fully styled
 * dropdown that matches the dark glass-morphism design system.
 *
 * Supports keyboard navigation, click-outside, scroll-into-view,
 * two sizes (sm for filter bars, md for forms), and portal-free
 * positioning via absolute + z-index.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import Check from 'lucide-react/dist/esm/icons/check';
import SearchIcon from 'lucide-react/dist/esm/icons/search';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  hint?: string;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Text shown when no value is selected (value === '') */
  placeholder?: string;
  /** Tailwind size preset */
  size?: 'sm' | 'md';
  /** Additional classes on the outer wrapper */
  className?: string;
  /** Disable interaction */
  disabled?: boolean;
  /** Show a search input inside the dropdown to filter options */
  searchable?: boolean;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
}

const SIZE_CLASSES = {
  sm: {
    trigger:
      'px-3 py-2.5 text-xs rounded-xl',
    menu: 'text-xs rounded-xl',
    option: 'px-3 py-2 text-xs',
  },
  md: {
    trigger:
      'px-3 py-2.5 text-sm rounded-xl',
    menu: 'text-sm rounded-xl',
    option: 'px-3 py-2.5 text-sm',
  },
} as const;

const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  size = 'md',
  className = '',
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search…',
}) => {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const s = SIZE_CLASSES[size];

  // ── Derived ──
  const selectedOption = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || !search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, searchable, search]);

  // ── Click-outside ──
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Scroll highlighted item into view ──
  useEffect(() => {
    if (!open || highlightIdx < 0) return;
    const menu = menuRef.current;
    if (!menu) return;
    const optionElements = menu.querySelectorAll('[role="option"]');
    const item = optionElements[highlightIdx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, open]);

  // ── Reset highlight & search when menu opens / focus search input ──
  useEffect(() => {
    if (open) {
      setSearch('');
      const idx = options.findIndex((o) => o.value === value);
      setHighlightIdx(idx >= 0 ? idx : 0);
      if (searchable) {
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset highlight when filtered list changes ──
  useEffect(() => {
    if (open) setHighlightIdx(0);
  }, [filteredOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard ──
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      const visibleOpts = filteredOptions;

      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (!open) {
            setOpen(true);
          } else if (highlightIdx >= 0 && highlightIdx < visibleOpts.length) {
            const picked = visibleOpts[highlightIdx];
            if (picked && !picked.disabled) {
              onChange(picked.value);
              setOpen(false);
              triggerRef.current?.focus();
            }
          }
          break;
        case ' ':
          if (searchable && open) break;
          e.preventDefault();
          if (!open) {
            setOpen(true);
          } else if (highlightIdx >= 0 && highlightIdx < visibleOpts.length) {
            const picked = visibleOpts[highlightIdx];
            if (picked && !picked.disabled) {
              onChange(picked.value);
              setOpen(false);
              triggerRef.current?.focus();
            }
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!open) {
            setOpen(true);
          } else {
            setHighlightIdx((i) => Math.min(i + 1, visibleOpts.length - 1));
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (open) {
            setHighlightIdx((i) => Math.max(i - 1, 0));
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
          break;
        case 'Tab':
          if (open) setOpen(false);
          break;
        default:
          break;
      }
    },
    [disabled, open, highlightIdx, filteredOptions, onChange, searchable],
  );

  const handleSelect = useCallback(
    (opt: SelectOption) => {
      if (opt.disabled) return;
      onChange(opt.value);
      setOpen(false);
      setSearch('');
      triggerRef.current?.focus();
    },
    [onChange],
  );

  const toggleOpen = useCallback(() => {
    if (!disabled) setOpen((prev) => !prev);
  }, [disabled]);

  return (
    <div
      ref={wrapperRef}
      className={`relative ${className}`}
      onKeyDown={handleKeyDown}
    >
      {/* ── Trigger ── */}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={toggleOpen}
        className={`
          w-full flex items-center justify-between gap-2
          bg-surface-primary border border-subtle
          outline-none transition-all duration-200 select-none
          ${s.trigger}
          ${disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:bg-surface-secondary hover:border-default focus:border-brand/40 focus:bg-surface-secondary'
          }
          ${open ? 'border-brand/40 bg-surface-secondary' : ''}
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${selectedOption ? 'text-content' : 'text-content-muted'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={size === 'sm' ? 12 : 14}
          className={`shrink-0 text-content-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {/* ── Dropdown ── */}
      {open && (
        <div
          ref={menuRef}
          role="listbox"
          className={`
            absolute z-dropdown left-0 right-0 mt-1.5
            bg-surface-page border border-default
            shadow-[0_12px_40px_rgba(0,0,0,0.5)]
            backdrop-blur-xl
            p-1
            animate-scale-in
            ${s.menu}
          `}
        >
          {searchable && (
            <div className="px-1 pb-1 mb-1 border-b border-subtle">
              <div className="relative">
                <SearchIcon
                  size={size === 'sm' ? 12 : 14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted pointer-events-none"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className={`
                    w-full bg-surface-primary border border-subtle rounded-lg
                    outline-none transition-colors duration-200
                    text-content placeholder:text-content-muted
                    focus:border-brand/40 focus:bg-surface-secondary
                    ${size === 'sm' ? 'pl-7 pr-2.5 py-1.5 text-xs' : 'pl-8 pr-3 py-2 text-sm'}
                  `}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          <div className="max-h-[240px] overflow-y-auto">
            {filteredOptions.map((opt, idx) => {
              const isSelected = opt.value === value;
              const isHighlighted = idx === highlightIdx;
              const isDisabled = !!opt.disabled;
              return (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  onClick={() => handleSelect(opt)}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  className={`
                    w-full flex items-center justify-between gap-2
                    rounded-lg outline-none select-none
                    transition-colors duration-100
                    ${s.option}
                    ${isDisabled
                      ? 'opacity-50 cursor-not-allowed text-content-muted'
                      : isSelected
                        ? 'bg-brand/15 text-primary cursor-pointer'
                        : isHighlighted
                          ? 'bg-surface-secondary text-content cursor-pointer'
                          : 'bg-transparent text-content-secondary hover:bg-surface-primary cursor-pointer'
                    }
                  `}
                >
                  <span className="truncate">
                    {opt.label}
                    {opt.hint ? (
                      <span className="ml-2 text-2xs text-content-muted">{opt.hint}</span>
                    ) : null}
                  </span>
                  {isSelected && !isDisabled && (
                    <Check size={12} className="shrink-0 text-primary" />
                  )}
                </div>
              );
            })}

            {filteredOptions.length === 0 && (
              <div className="px-3 py-4 text-center text-content-faint text-xs">
                {search ? 'No matches' : 'No options'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Select);
